import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Spinner } from '@atoms/Spinner';
import { financeService } from '@services/financeService';
import { formatCurrency, formatDate } from '@utils/formatters';

export function AccountingSection() {
  const queryClient = useQueryClient();
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [description, setDescription] = useState('');

  const { data: customers, isLoading: customersLoading } = useQuery({
    queryKey: ['wholesaler', 'customers'],
    queryFn: () => financeService.getCustomers(),
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['wholesaler', 'customer-history', selectedRestaurantId],
    queryFn: () => financeService.getCustomerHistory(selectedRestaurantId),
    enabled: Boolean(selectedRestaurantId),
  });

  const paymentMutation = useMutation({
    mutationFn: () =>
      financeService.recordPayment({
        restaurantId: selectedRestaurantId,
        amount: Number(amount),
        paymentMethod,
        description: description || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'customers'] });
      queryClient.invalidateQueries({
        queryKey: ['wholesaler', 'customer-history', selectedRestaurantId],
      });
      setAmount('');
      setDescription('');
    },
  });

  if (customersLoading) return <Spinner />;

  const selectedCustomer = customers?.find((c) => c.restaurantId === selectedRestaurantId);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Muhasebe</h2>

      <Card>
        <label className="mb-1 block text-sm font-medium">Müşteri (Restoran)</label>
        <select
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={selectedRestaurantId}
          onChange={(e) => setSelectedRestaurantId(e.target.value)}
        >
          <option value="">Restoran seçin</option>
          {(customers ?? []).map((c) => (
            <option key={c.id} value={c.restaurantId}>
              {c.restaurantName} — {formatCurrency(c.balance)}
            </option>
          ))}
        </select>
      </Card>

      {selectedRestaurantId && (
        <>
          <Card>
            <p className="text-sm text-gray-500">Cari Bakiye</p>
            <p className="text-3xl font-bold text-red-600">
              {formatCurrency(history?.balance ?? selectedCustomer?.balance ?? 0)}
            </p>
          </Card>

          <Card className="grid gap-3 md:grid-cols-4">
            <Input label="Tutar" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <div>
              <label className="mb-1 block text-sm font-medium">Ödeme Yöntemi</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="cash">Nakit</option>
                <option value="credit_card">Kredi Kartı</option>
                <option value="partial">Kısmi</option>
              </select>
            </div>
            <Input
              label="Açıklama"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tahsilat"
            />
            <div className="flex items-end">
              <Button
                loading={paymentMutation.isPending}
                disabled={!amount || Number(amount) <= 0}
                onClick={() => paymentMutation.mutate()}
              >
                Ödeme Kaydet
              </Button>
            </div>
          </Card>

          <div className="space-y-2">
            <h3 className="font-medium">İşlem Geçmişi</h3>
            {historyLoading ? (
              <Spinner />
            ) : (history?.transactions ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">Henüz işlem yok.</p>
            ) : (
              (history?.transactions ?? []).map((tx) => (
                <Card key={tx.id} className="flex justify-between text-sm">
                  <div>
                    <p>{tx.description ?? (tx.type === 'debt' ? 'Borç' : 'Alacak')}</p>
                    <p className="text-xs text-gray-500">{formatDate(tx.createdAt)}</p>
                  </div>
                  <span className={tx.type === 'debt' ? 'text-red-600' : 'text-green-600'}>
                    {tx.type === 'debt' ? '-' : '+'}
                    {formatCurrency(tx.amount)}
                  </span>
                </Card>
              ))
            )}
          </div>
        </>
      )}

      {!selectedRestaurantId && (customers ?? []).length === 0 && (
        <p className="text-sm text-gray-500">Henüz cari hesabı olan müşteri yok.</p>
      )}
    </div>
  );
}
